// Home.js — News & Reviews
import { useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  addDoc,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  query,
  where,
  limit,
  startAfter,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { AuthContext } from "../AuthContext";
import { db } from "../firebase";
import { normalizeRole } from "../utils/roleUtils";
import { isAdminUser } from "../config/admins";
import { useTranslation } from "../i18n/LanguageContext";
import {
  MOOD_OPTIONS,
  GENRE_GROUPS,
  EVENT_STATUS,
  resolveList,
  resolveStatus,
  toggleInList,
} from "../config/eventOptions";
import {
  fetchAiSuggestions,
  buildDraftFromSuggestion,
  confidenceLabel,
} from "../services/aiAssistant";
import { ENABLE_TICKET_LINKS } from "../config/features";
import FilterBar from "../components/filters/FilterBar";
import MultiSelect from "../components/filters/MultiSelect";
import SearchableSelect from "../components/filters/SearchableSelect";
import ScopeTabs from "../components/ScopeTabs";
import Drawer from "../components/Drawer";
import ContentCard from "../components/ContentCard";
import { getEventFallbackImage } from "../utils/eventImages";
import { formatTimestampDate } from "../utils/formatDate";

function formatTimestamp(ts, language) {
  return formatTimestampDate(ts, language);
}

// Bounded page size for each Firestore read channel (approved/own/drafts).
const ENTRIES_PAGE_SIZE = 20;

// AI source-type → target collection. "Event idea" is saved as a NEWS draft.
const targetCollectionFor = (sourceType) => (sourceType === "review" ? "reviews" : "news");

function Home() {
  const { user, isAdmin } = useContext(AuthContext);
  const { t, language } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [buddyIds, setBuddyIds] = useState([]);
  const [viewMode, setViewMode] = useState("review");
  const [activeTab, setActiveTab] = useState("public");

  // Filter state
  const [filterCity, setFilterCity] = useState("");
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedMoods, setSelectedMoods] = useState([]);
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  // Post form state
  const [editId, setEditId] = useState(null);
  const [formVisible, setFormVisible] = useState(false);
  const [title, setTitle] = useState("");
  const [city, setCity] = useState("");
  const [formGenres, setFormGenres] = useState([]);
  const [formMoods, setFormMoods] = useState([]);
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [ticketUrl, setTicketUrl] = useState("");

  // AI Assistant state (admin-only)
  const [aiOpen, setAiOpen] = useState(false);
  const [aiCity, setAiCity] = useState("");
  const [aiGenres, setAiGenres] = useState([]);
  const [aiStart, setAiStart] = useState("");
  const [aiEnd, setAiEnd] = useState("");
  const [aiSourceType, setAiSourceType] = useState("news");
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState([]);
  const [entriesError, setEntriesError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [hasMoreEntries, setHasMoreEntries] = useState(false);
  const [isLoadingMoreEntries, setIsLoadingMoreEntries] = useState(false);
  const entriesListRef = useRef([]);
  const userMapRef = useRef({});
  const entryCursorsRef = useRef({});
  const entryChannelsDoneRef = useRef({});

  const collectionName = viewMode === "news" ? "news" : "reviews";

  const cityOptions = useMemo(
    () => [...new Set(entries.map((e) => e.city).filter(Boolean))].sort(),
    [entries]
  );
  const formatConfidence = useCallback(
    (value) => {
      const label = confidenceLabel(value);
      return t(`common.confidence.${label.toLowerCase()}`, label);
    },
    [t]
  );

  const reloadEntries = async (name) => {
    const data = await fetchScopedEntries(name);
    setEntries(data);
  };

  useEffect(() => {
    if (user) {
      getDoc(doc(db, "users", user.uid)).then((snap) => {
        if (snap.exists()) {
          setBuddyIds(snap.data().friends || []);
        }
      });
    }
  }, [user]);

  // Each query here must line up with a matching branch in firestore.rules' shared
  // news/reviews read rule — Firestore rejects an entire list() if it could return
  // even one document the requester isn't allowed to see, so we can no longer fetch
  // the whole collection and filter client-side.
  const fetchScopedEntries = useCallback(
    async (name) => {
      const entriesRef = collection(db, name);
      const queries = [query(entriesRef, where("status", "==", EVENT_STATUS.APPROVED))];
      if (user) {
        queries.push(query(entriesRef, where("creatorId", "==", user.uid)));
      }
      if (isAdmin) {
        queries.push(query(entriesRef, where("status", "==", EVENT_STATUS.DRAFT)));
      }
      const snaps = await Promise.all(queries.map((q) => getDocs(q)));
      const byId = new Map();
      snaps.forEach((snap) => snap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() })));
      return [...byId.values()];
    },
    [user, isAdmin]
  );

  // Fetches one bounded page (ENTRIES_PAGE_SIZE) per read channel (approved/own/drafts) and
  // merges it into the existing entries list. `isReset` starts every channel over from
  // scratch (used on identity/viewMode-relevant changes); otherwise each channel resumes
  // from its own Firestore cursor, so "Load more" never re-reads documents already fetched.
  //
  // Trade-off: no orderBy() here, same reasoning as Events.js — these single-field
  // equality queries don't need a composite index today, but orderBy(timestamp) would,
  // and that index isn't deployed. limit()+startAfter() still gives real, bounded cursor
  // pagination via Firestore's default document-ID ordering. See chat report for details.
  const loadEntriesPage = useCallback(
    async (isReset) => {
      if (isReset) {
        entryCursorsRef.current = {};
        entryChannelsDoneRef.current = {};
        setEntriesError("");
      }

      const name = viewMode === "news" ? "news" : "reviews";
      const entriesCol = collection(db, name);

      // Each channel here must line up with a matching branch in firestore.rules' shared
      // news/reviews read rule — Firestore rejects an entire list() if it could return
      // even one document the requester isn't allowed to see, so we fetch bounded pages
      // per channel instead of the whole collection.
      const channels = [
        { key: "approved", baseQuery: () => query(entriesCol, where("status", "==", EVENT_STATUS.APPROVED)) },
      ];
      if (user) {
        channels.push({ key: "own", baseQuery: () => query(entriesCol, where("creatorId", "==", user.uid)) });
      }
      if (isAdmin) {
        channels.push({ key: "drafts", baseQuery: () => query(entriesCol, where("status", "==", EVENT_STATUS.DRAFT)) });
      }

      const activeChannels = channels.filter((c) => !entryChannelsDoneRef.current[c.key]);
      if (activeChannels.length === 0) {
        setHasMoreEntries(false);
        return;
      }

      try {
        const snaps = await Promise.all(
          activeChannels.map((c) => {
            const cursor = entryCursorsRef.current[c.key];
            const pageQuery = cursor
              ? query(c.baseQuery(), startAfter(cursor), limit(ENTRIES_PAGE_SIZE))
              : query(c.baseQuery(), limit(ENTRIES_PAGE_SIZE));
            return getDocs(pageQuery);
          })
        );

        const byId = new Map((isReset ? [] : entriesListRef.current).map((e) => [e.id, e]));
        activeChannels.forEach((c, idx) => {
          const snap = snaps[idx];
          snap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
          if (snap.docs.length > 0) {
            entryCursorsRef.current[c.key] = snap.docs[snap.docs.length - 1];
          }
          if (snap.docs.length < ENTRIES_PAGE_SIZE) {
            entryChannelsDoneRef.current[c.key] = true;
          }
        });

        const data = [...byId.values()];
        entriesListRef.current = data;
        setEntries(data);
        setHasMoreEntries(channels.some((c) => !entryChannelsDoneRef.current[c.key]));

        const ids = [...new Set(data.map((e) => e.creatorId))];
        const missingIds = ids.filter((id) => !userMapRef.current[id]);
        if (missingIds.length > 0) {
          const newUsers = {};
          for (const uid of missingIds) {
            const uSnap = await getDoc(doc(db, "users", uid));
            if (uSnap.exists()) newUsers[uid] = uSnap.data();
          }
          userMapRef.current = { ...userMapRef.current, ...newUsers };
          setUserMap(userMapRef.current);
        }
      } catch (err) {
        console.error("Failed to load news/reviews:", err);
        setEntriesError(t("home.loadError", "Couldn't load news/reviews. Please try again."));
      }
    },
    [viewMode, user, isAdmin, t]
  );

  useEffect(() => {
    loadEntriesPage(true);
  }, [viewMode, user, isAdmin, reloadToken, loadEntriesPage]);

  const handleLoadMoreEntries = async () => {
    if (isLoadingMoreEntries || !hasMoreEntries) return;
    setIsLoadingMoreEntries(true);
    try {
      await loadEntriesPage(false);
    } finally {
      setIsLoadingMoreEntries(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setCity("");
    setFormGenres([]);
    setFormMoods([]);
    setDescription("");
    setImageFile(null);
    setTicketUrl("");
    setEditId(null);
    setFormVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !city || formGenres.length === 0 || !description || !user) return;

    let uploadedUrl = "";
    if (imageFile) {
      const storage = getStorage();
      const imageRef = ref(storage, `uploads/${user.uid}-${Date.now()}-${imageFile.name}`);
      await uploadBytes(imageRef, imageFile);
      uploadedUrl = await getDownloadURL(imageRef);
    }

    const contentData = {
      title,
      city,
      genres: formGenres,
      moods: formMoods,
      description,
    };
    if (uploadedUrl) contentData.imageUrl = uploadedUrl;
    // Ticket links are a locked premium feature — only saved when enabled.
    if (ENABLE_TICKET_LINKS && ticketUrl) contentData.ticketUrl = ticketUrl;

    if (editId) {
      // Editing preserves existing status/source (an AI draft stays a draft).
      await updateDoc(doc(db, collectionName, editId), contentData);
    } else {
      // Manual creation is published directly.
      await addDoc(collection(db, collectionName), {
        ...contentData,
        imageUrl: uploadedUrl,
        creatorId: user.uid,
        status: EVENT_STATUS.APPROVED,
        source: "manual",
        publishedAt: new Date().toISOString(),
        timestamp: serverTimestamp(),
      });
    }

    resetForm();
    await reloadEntries(collectionName);
  };

  const handleEdit = (entry) => {
    setEditId(entry.id);
    setTitle(entry.title || "");
    setCity(entry.city || "");
    setFormGenres(resolveList(entry.genres, entry.genre));
    setFormMoods(resolveList(entry.moods, entry.mood));
    setDescription(entry.description || "");
    setTicketUrl(entry.ticketUrl || "");
    setFormVisible(true);
  };

  const handleDelete = async (entryId) => {
    await deleteDoc(doc(db, collectionName, entryId));
    setEntries((prev) => prev.filter((e) => e.id !== entryId));
  };

  const handleApprove = async (entry) => {
    // Approved items appear in Public News/Reviews.
    await updateDoc(doc(db, collectionName, entry.id), {
      status: EVENT_STATUS.APPROVED,
      reviewedBy: user.uid,
      approvedAt: serverTimestamp(),
    });
    setEntries((prev) =>
      prev.map((e) => (e.id === entry.id ? { ...e, status: EVENT_STATUS.APPROVED } : e))
    );
  };

  const handleReject = async (entry) => {
    // Rejected items stay hidden (kept in Firestore for audit).
    await updateDoc(doc(db, collectionName, entry.id), {
      status: EVENT_STATUS.REJECTED,
      reviewedBy: user.uid,
      rejectedAt: serverTimestamp(),
    });
    setEntries((prev) =>
      prev.map((e) => (e.id === entry.id ? { ...e, status: EVENT_STATUS.REJECTED } : e))
    );
  };

  // ---- AI Assistant (admin-only) ----
  const openAi = () => {
    setAiCity(filterCity || "");
    setAiGenres([]);
    setAiStart("");
    setAiEnd("");
    setAiSourceType(viewMode === "news" ? "news" : "review");
    setAiText("");
    setAiResults([]);
    setAiLoading(false);
    setAiOpen(true);
  };

  const runAi = async () => {
    setAiLoading(true);
    try {
      const res = await fetchAiSuggestions({
        text: aiText,
        city: aiCity,
        genres: aiGenres,
        startDate: aiStart,
        endDate: aiEnd,
        sourceType: aiSourceType,
      });
      setAiResults(res);
    } finally {
      setAiLoading(false);
    }
  };

  const saveSuggestionAsDraft = async (suggestion) => {
    if (!user) return;
    const target = targetCollectionFor(aiSourceType);
    const draft = buildDraftFromSuggestion(suggestion, { sourceType: aiSourceType, adminUid: user.uid });
    await addDoc(collection(db, target), { ...draft, timestamp: serverTimestamp() });
    setAiResults((prev) => prev.filter((s) => s !== suggestion));
    if (target === collectionName) await reloadEntries(collectionName);
  };

  const saveAllAsDrafts = async () => {
    if (!user || aiResults.length === 0) return;
    const target = targetCollectionFor(aiSourceType);
    await Promise.all(
      aiResults.map((s) =>
        addDoc(collection(db, target), {
          ...buildDraftFromSuggestion(s, { sourceType: aiSourceType, adminUid: user.uid }),
          timestamp: serverTimestamp(),
        })
      )
    );
    setAiResults([]);
    if (target === collectionName) await reloadEntries(collectionName);
  };

  const filtered = entries.filter((e) => {
    const entryDate = (e.publishedAt || "").slice(0, 10);
    const genres = resolveList(e.genres, e.genre);
    const moods = resolveList(e.moods, e.mood);
    return (
      (!filterCity || (e.city || "").toLowerCase().includes(filterCity.toLowerCase())) &&
      (selectedGenres.length === 0 || genres.some((g) => selectedGenres.includes(g))) &&
      (selectedMoods.length === 0 || moods.some((m) => selectedMoods.includes(m))) &&
      (!filterStartDate || entryDate >= filterStartDate) &&
      (!filterEndDate || entryDate <= filterEndDate)
    );
  });

  // Only approved entries appear in the public / buddy / own feeds.
  const approvedEntries = filtered.filter((e) => resolveStatus(e) === EVENT_STATUS.APPROVED);

  const publicItems = approvedEntries.filter((e) => {
    const cr = userMap[e.creatorId];
    return (
      normalizeRole(cr?.role) === "artist" ||
      normalizeRole(cr?.role) === "organizer" ||
      isAdminUser(cr)
    );
  });

  const buddyItems = approvedEntries.filter(
    (e) =>
      buddyIds.includes(e.creatorId) &&
      normalizeRole(userMap[e.creatorId]?.role) === "partybuddy" &&
      !isAdminUser(userMap[e.creatorId]) &&
      e.creatorId !== user?.uid
  );

  const ownItems = approvedEntries.filter((e) => e.creatorId === user?.uid);

  // Drafts awaiting review (admin-only tab). Rejected entries stay hidden.
  const draftItems = filtered.filter((e) => resolveStatus(e) === EVENT_STATUS.DRAFT);

  // Active filter tags for the collapsed bar.
  const activeTags = [];
  if (filterCity) activeTags.push({ key: "city", label: filterCity, onRemove: () => setFilterCity("") });
  selectedGenres.forEach((g) =>
    activeTags.push({ key: `g-${g}`, label: g, onRemove: () => setSelectedGenres((p) => p.filter((x) => x !== g)) })
  );
  selectedMoods.forEach((m) =>
    activeTags.push({ key: `m-${m}`, label: m, onRemove: () => setSelectedMoods((p) => p.filter((x) => x !== m)) })
  );
  if (filterStartDate || filterEndDate) {
    activeTags.push({
      key: "dates",
      label: `${filterStartDate || "…"} – ${filterEndDate || "…"}`,
      onRemove: () => {
        setFilterStartDate("");
        setFilterEndDate("");
      },
    });
  }

  const resetFilters = () => {
    setFilterCity("");
    setSelectedGenres([]);
    setSelectedMoods([]);
    setFilterStartDate("");
    setFilterEndDate("");
  };

  const tabs = [
    { key: "public", label: t("home.tabPublic", "Public"), count: publicItems.length },
    { key: "buddies", label: t("home.tabBuddies", "Buddies"), count: buddyItems.length },
    { key: "yours", label: t("home.tabYours", "Yours"), count: ownItems.length },
    ...(isAdmin ? [{ key: "drafts", label: t("home.tabDrafts", "Drafts"), count: draftItems.length }] : []),
  ];
  const safeTab = activeTab === "drafts" && !isAdmin ? "public" : activeTab;
  const activeList =
    safeTab === "public"
      ? publicItems
      : safeTab === "buddies"
      ? buddyItems
      : safeTab === "drafts"
      ? draftItems
      : ownItems;

  const renderCard = (entry, index = 0) => {
    const cr = userMap[entry.creatorId];
    const genres = resolveList(entry.genres, entry.genre);
    const moods = resolveList(entry.moods, entry.mood);
    const status = resolveStatus(entry);
    const isDraftTab = safeTab === "drafts";
    const isAi = entry.source === "ai_suggestion";
    const badges = [
      ...(status !== EVENT_STATUS.APPROVED ? [{ label: t(`home.status.${status}`, status.toUpperCase()), tone: "draft" }] : []),
      ...(isDraftTab && isAi ? [{ label: "AI", tone: "ai" }] : []),
      ...genres.map((g) => ({ label: g, tone: "genre" })),
      ...moods.map((m) => ({ label: m, tone: "mood" })),
      ...(entry.city ? [{ label: entry.city, tone: "city" }] : []),
    ];

    const excerpt =
      isDraftTab && entry.notes ? `${entry.description || ""}\n\n${entry.notes}` : entry.description;

    let actions = null;
    if (isDraftTab) {
      actions = (
        <>
          {entry.sourceUrl && (
            <a
              href={entry.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-sky-300 transition hover:text-sky-200"
            >
              {t("home.source", "Source")}
            </a>
          )}
          <button
            onClick={() => handleEdit(entry)}
            className="text-xs font-semibold text-yellow-400 transition hover:text-yellow-300"
          >
            {t("home.edit", "Edit")}
          </button>
          <button
            onClick={() => handleReject(entry)}
            className="text-xs font-semibold text-red-400 transition hover:text-red-300"
          >
            {t("home.reject", "Reject")}
          </button>
          <button
            onClick={() => handleApprove(entry)}
            className="text-xs font-semibold text-emerald-400 transition hover:text-emerald-300"
          >
            {t("home.approve", "Approve")}
          </button>
        </>
      );
    } else if (safeTab === "yours" && entry.creatorId === user?.uid) {
      actions = (
        <>
          <button
            onClick={() => handleEdit(entry)}
            className="rounded-2xl bg-slate-700/90 px-4 py-2 text-xs font-semibold text-slate-200 transition hover:bg-slate-600"
          >
            {t("home.edit", "Edit")}
          </button>
          <button
            onClick={() => handleDelete(entry.id)}
            className="rounded-2xl bg-rose-500/90 px-4 py-2 text-xs font-semibold text-white transition hover:bg-rose-500"
          >
            {t("home.delete", "Delete")}
          </button>
        </>
      );
    }

    return (
      <ContentCard
        key={entry.id}
        imageUrl={entry.imageUrl}
        fallbackImageUrl={getEventFallbackImage({
          id: entry.id || entry.title,
          genres,
          moods,
        })}
        badges={badges}
        title={entry.title}
        subtitle={isDraftTab && isAi ? `${t("home.confidence", "Confidence")}: ${formatConfidence(entry.confidence)}` : undefined}
        excerpt={excerpt}
        authorName={isAi || isAdminUser(cr) ? "" : cr?.username || ""}
        authorId={entry.creatorId}
        authorPhotoUrl={isAi || isAdminUser(cr) ? "" : cr?.photoUrl || ""}
        postedLabel={formatTimestamp(entry.timestamp, language) ? `${t("home.postedOn", "Posted")} ${formatTimestamp(entry.timestamp, language)}` : ""}
        ticketUrl={entry.ticketUrl}
        ticketLabel={t("ticket.buy", "Buy tickets")}
        noImageLabel={t("event.noImage", "No image")}
        authorPrefix={t("home.by", "By")}
        actions={actions}
      />
    );
  };

  return (
    <div className="min-h-screen bg-slate-950/70 p-4 md:p-6">
      {/* Header + toggle + collapsible filter bar */}
      <div className="mx-auto mb-4 flex max-w-7xl flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-white">{t("home.title", "News & Reviews")}</h1>
        <div className="flex overflow-hidden rounded-full border border-slate-700">
          <button
            onClick={() => setViewMode("news")}
            className={`px-4 py-1.5 text-sm font-semibold transition ${
              viewMode === "news" ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white" : "bg-slate-800/80 text-slate-300 hover:bg-slate-700"
            }`}
          >
            {t("home.createNews", "News")}
          </button>
          <button
            onClick={() => setViewMode("review")}
            className={`px-4 py-1.5 text-sm font-semibold transition ${
              viewMode === "review" ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white" : "bg-slate-800/80 text-slate-300 hover:bg-slate-700"
            }`}
          >
            {t("home.createReview", "Reviews")}
          </button>
        </div>
        <FilterBar activeTags={activeTags} onClearAll={resetFilters}>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-300">{t("filters.city", "City")}</span>
              <SearchableSelect value={filterCity} onChange={setFilterCity} options={cityOptions} placeholder={t("filters.city", "City")} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-300">{t("filters.from", "From")}</span>
                <input
                  type="date"
                  className="w-full rounded-2xl border border-slate-700 bg-slate-900/95 px-3 py-2 text-sm text-white outline-none transition focus:border-sky-400"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-300">{t("filters.to", "To")}</span>
                <input
                  type="date"
                  className="w-full rounded-2xl border border-slate-700 bg-slate-900/95 px-3 py-2 text-sm text-white outline-none transition focus:border-sky-400"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </label>
            </div>
            <div>
              <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-300">{t("filters.mood", "Mood")}</span>
              <MultiSelect
                options={MOOD_OPTIONS}
                selected={selectedMoods}
                onToggle={(value) => setSelectedMoods((prev) => toggleInList(prev, value))}
              />
            </div>
            <div className="md:col-span-2">
              <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-300">{t("filters.genre", "Genre")}</span>
              <MultiSelect
                groups={GENRE_GROUPS}
                selected={selectedGenres}
                onToggle={(value) => setSelectedGenres((prev) => toggleInList(prev, value))}
              />
            </div>
          </div>
        </FilterBar>
        {isAdmin && (
          <button
            onClick={openAi}
            className="rounded-2xl border border-violet-500/60 bg-violet-600/20 px-4 py-2 text-sm font-semibold text-violet-100 transition hover:bg-violet-600/40"
          >
            ✨ {t("home.aiAssistant", "AI Assistant")}
          </button>
        )}
        {user && (
          <button
            onClick={() => {
              resetForm();
              setFormVisible(true);
            }}
            className="rounded-2xl bg-gradient-to-r from-cyan-500 to-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:from-cyan-400 hover:to-sky-500"
          >
            + {t("home.createPost", "Post")}
          </button>
        )}
      </div>

      {/* Scope tabs */}
      <div className="mx-auto mb-4 max-w-7xl">
        <ScopeTabs tabs={tabs} active={safeTab} onChange={setActiveTab} />
      </div>

      {entriesError && (
        <div className="mx-auto mb-4 max-w-7xl">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
            <span>⚠️ {entriesError}</span>
            <button
              type="button"
              onClick={() => setReloadToken((n) => n + 1)}
              className="rounded-lg border border-red-400/60 bg-red-500/20 px-3 py-1 text-xs font-semibold text-red-100 transition hover:bg-red-500/30"
            >
              {t("common.retry", "Retry")}
            </button>
          </div>
        </div>
      )}

      {/* Card grid */}
      <section className="mx-auto max-w-7xl">
        {activeList.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-700 bg-slate-800/70 p-6 text-center text-slate-300">
            {safeTab === "drafts" ? t("home.emptyDrafts", "No drafts awaiting review.") : t("home.emptyEntries", "No entries here yet.")}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {activeList.map((entry, index) => renderCard(entry, index))}
          </div>
        )}

        {hasMoreEntries && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={handleLoadMoreEntries}
              disabled={isLoadingMoreEntries}
              className="rounded-2xl border border-slate-600 bg-slate-800/80 px-6 py-2.5 text-sm font-semibold text-slate-100 transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoadingMoreEntries
                ? t("common.loadingMore", "Loading…")
                : t("common.loadMore", "Load more")}
            </button>
          </div>
        )}
      </section>

      {/* Creation / edit drawer */}
      <Drawer
        open={formVisible}
        title={editId ? t("home.edit", "Edit") : viewMode === "news" ? t("home.drawerNews", "New news") : t("home.drawerReview", "New review")}
        onClose={resetForm}
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={resetForm}
              className="rounded-xl border border-slate-600 bg-slate-700 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-600"
            >
              {t("home.cancel", "Cancel")}
            </button>
            <button
              type="submit"
              form="post-form"
              className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-lg transition hover:from-blue-500 hover:to-indigo-500"
            >
              {editId ? t("home.update", "Update") : t("home.save", "Save")}
            </button>
          </div>
        }
      >
        <form id="post-form" onSubmit={handleSubmit} className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("home.fieldTitle", "Title")}</span>
            <input
              className="w-full rounded-lg border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400"
              placeholder={t("home.fieldTitle", "Title")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("filters.city", "City")}</span>
            <SearchableSelect value={city} onChange={setCity} options={cityOptions} placeholder={t("filters.city", "City")} />
          </label>
          <div>
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("home.fieldMoods", "Moods")}</span>
            <MultiSelect
              options={MOOD_OPTIONS}
              selected={formMoods}
              onToggle={(value) => setFormMoods((prev) => toggleInList(prev, value))}
            />
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("home.fieldGenres", "Genres")}</span>
            <MultiSelect
              groups={GENRE_GROUPS}
              selected={formGenres}
              onToggle={(value) => setFormGenres((prev) => toggleInList(prev, value))}
            />
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("home.fieldDescription", "Description")}</span>
            <textarea
              className="w-full rounded-lg border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400"
              placeholder={t("home.fieldDescription", "Description")}
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("home.fieldImage", "Image")}</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
              className="w-full text-gray-300 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-blue-500"
            />
          </label>
          {/* Ticket link — locked premium feature (ENABLE_TICKET_LINKS). */}
          <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-slate-300">🎟️ {t("ticket.add", "Add ticket link")}</span>
              {!ENABLE_TICKET_LINKS && (
                <span className="rounded-full border border-amber-400/40 bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200">
                  {t("ticket.locked", "Premium feature – coming soon")}
                </span>
              )}
            </div>
            <input
              className="mt-2 w-full rounded-lg border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="https://tickets…"
              value={ticketUrl}
              onChange={(e) => setTicketUrl(e.target.value)}
              disabled={!ENABLE_TICKET_LINKS}
            />
          </div>
        </form>
      </Drawer>

      {/* AI Assistant drawer (admin-only) */}
      <Drawer
        open={aiOpen}
        title={t("home.aiAssistant", "AI Assistant")}
        onClose={() => setAiOpen(false)}
        footer={
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setAiOpen(false)}
              className="rounded-xl border border-slate-600 bg-slate-700 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-600"
            >
              {t("home.close", "Close")}
            </button>
          </div>
        }
      >
        <p className="rounded-xl border border-violet-500/40 bg-violet-600/10 p-3 text-[11px] text-violet-200">
          {t("home.mockNotice", "Mock mode — no internet search or AI is connected yet. Suggestions are saved as drafts only and must be approved before they appear publicly.")}
        </p>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-300">{t("home.pasteAtoms", "Paste Atoms results (JSON) or text")}</span>
          <textarea
            className="w-full rounded-lg border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400"
            placeholder={t("home.pasteAtomsPlaceholder", 'Paste Atoms JSON, e.g. [{ "title": "…", "city": "…", "genres": [] }]')}
            rows={4}
            value={aiText}
            onChange={(e) => setAiText(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-300">{t("filters.city", "City")}</span>
          <SearchableSelect value={aiCity} onChange={setAiCity} options={cityOptions} placeholder={t("filters.city", "City")} />
        </label>

        <div>
          <span className="mb-1 block text-xs font-medium text-slate-300">{t("home.sourceType", "Source type")}</span>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "news", label: t("home.sourceTypeNews", "News") },
              { key: "review", label: t("home.sourceTypeReview", "Review") },
              { key: "event_idea", label: t("home.sourceTypeEventIdea", "Event idea") },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setAiSourceType(option.key)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  aiSourceType === option.key
                    ? "bg-indigo-500 text-white"
                    : "bg-slate-700 text-slate-200 hover:bg-slate-600"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {aiSourceType === "event_idea" && (
            <p className="mt-1 text-[11px] text-slate-400">{t("home.eventIdeaHint", "Event ideas are saved as News drafts, tagged.")}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("filters.from", "From")}</span>
            <input
              type="date"
              className="w-full rounded-lg border border-slate-600 bg-slate-700 p-3 text-white outline-none transition focus:ring-2 focus:ring-blue-400"
              value={aiStart}
              onChange={(e) => setAiStart(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("filters.to", "To")}</span>
            <input
              type="date"
              className="w-full rounded-lg border border-slate-600 bg-slate-700 p-3 text-white outline-none transition focus:ring-2 focus:ring-blue-400"
              value={aiEnd}
              onChange={(e) => setAiEnd(e.target.value)}
            />
          </label>
        </div>

        <div>
          <span className="mb-1 block text-xs font-medium text-slate-300">{t("home.genreEventType", "Genre / event type")}</span>
          <MultiSelect
            groups={GENRE_GROUPS}
            selected={aiGenres}
            onToggle={(value) => setAiGenres((prev) => toggleInList(prev, value))}
          />
        </div>

        <button
          type="button"
          onClick={runAi}
          disabled={aiLoading}
          className="w-full rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:from-violet-500 hover:to-indigo-500 disabled:opacity-60"
        >
          {aiLoading ? t("home.searching", "Searching…") : t("home.findSuggestions", "Find suggestions")}
        </button>

        {/* Results */}
        {!aiLoading && aiResults.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">{aiResults.length} {t("home.suggestions", "suggestions")}</span>
              <button
                type="button"
                onClick={saveAllAsDrafts}
                className="rounded-lg bg-emerald-600/90 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-500"
              >
                {t("home.saveAll", "Save all as drafts")}
              </button>
            </div>
            {aiResults.map((suggestion, index) => (
              <div key={index} className="space-y-1 rounded-xl border border-slate-700 bg-slate-900/60 p-3">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-semibold text-white">{suggestion.title}</h4>
                  <span className="shrink-0 rounded-full bg-slate-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-200">
                    {formatConfidence(suggestion.confidence)}
                  </span>
                </div>
                <p className="text-[11px] text-slate-400">
                  {[suggestion.city, ...(suggestion.genres || []), ...(suggestion.moods || [])].filter(Boolean).join(" · ")}
                </p>
                <p className="text-xs text-slate-300">{suggestion.description}</p>
                {suggestion.notes && <p className="text-[11px] text-amber-300/80">{suggestion.notes}</p>}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => saveSuggestionAsDraft(suggestion)}
                    className="rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-600"
                  >
                    {t("home.saveDraft", "Save as draft")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {!aiLoading && aiResults.length === 0 && (
          <p className="text-xs text-slate-400">{t("home.noSuggestions", "No suggestions yet. Set your filters and run a search.")}</p>
        )}
      </Drawer>
    </div>
  );
}

export default Home;
